import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { MeshGradient } from "../components/AnimatedBackground";
import {
  ArrowLeft,
  Tag,
  Calendar,
  Sparkles,
  Bug,
  Zap,
  Shield,
} from "lucide-react";

interface ChangelogEntry {
  version: string;
  date: string;
  type: "major" | "minor" | "patch";
  highlights?: string;
  changes: {
    category: "feature" | "fix" | "improvement" | "security";
    description: string;
  }[];
}

const CHANGELOG: ChangelogEntry[] = [
  {
    version: "1.0.3",
    date: "2025-12-08",
    type: "minor",
    highlights:
      "NeonDB support, connection reliability improvements, and accurate database metrics",
    changes: [
      {
        category: "feature",
        description:
          "NeonDB (serverless PostgreSQL) support with automatic SSL and connection string authentication",
      },
      {
        category: "feature",
        description:
          "Loading indicator in Data Browser showing progress while schema and tables are being fetched",
      },
      {
        category: "fix",
        description:
          "Connection strings now persist correctly across app restarts for all database engines",
      },
      {
        category: "fix",
        description:
          "Reserved SQL keywords (user, order, group, etc.) now properly quoted in all queries",
      },
      {
        category: "fix",
        description:
          "Oracle table sizes now displayed accurately in Schema Visualizer metrics",
      },
      {
        category: "fix",
        description:
          "SQLite table size calculation corrected (was summing page numbers instead of counting pages)",
      },
      {
        category: "improvement",
        description:
          "Table Designer now supports NeonDB with PostgreSQL-compatible column types",
      },
      {
        category: "improvement",
        description:
          "Connection modal correctly detects and displays connection string mode when editing",
      },
    ],
  },
  {
    version: "1.0.2",
    date: "2025-12-07",
    type: "minor",
    highlights:
      "Oracle SQL support, Table Designer, and detachable query results",
    changes: [
      {
        category: "feature",
        description:
          "Oracle SQL database support with Instant Client and wallet configuration",
      },
      {
        category: "feature",
        description:
          "Table Designer: visually create, modify, and drop tables with full DDL preview",
      },
      {
        category: "feature",
        description:
          "DDL tab in Schema Visualizer showing CREATE TABLE statements",
      },
      {
        category: "feature",
        description: "Detachable query results panel that syncs across windows",
      },
      {
        category: "feature",
        description:
          "Generate scripts: CREATE, DROP, TRUNCATE, and INSERT statements",
      },
      {
        category: "feature",
        description: "Organize saved snippets into collapsible categories",
      },
      {
        category: "feature",
        description:
          "Query history detail view with one-click insert to editor",
      },
      {
        category: "feature",
        description: "Persistent query history across sessions (optional)",
      },
      {
        category: "improvement",
        description: "Toast notifications for errors and warnings",
      },
      {
        category: "improvement",
        description: "Redesigned color theme picker in settings",
      },
      {
        category: "improvement",
        description: "Sidebar states persist across sessions",
      },
      {
        category: "fix",
        description: "PostgreSQL tables showing incorrect row counts",
      },
      {
        category: "fix",
        description: "PostgreSQL date values not displaying correctly",
      },
      {
        category: "fix",
        description: "Oracle EXPLAIN PLAN now works properly",
      },
      {
        category: "fix",
        description: "Data Browser pagination showing wrong page count",
      },
      {
        category: "fix",
        description: "Detached results window sync issues",
      },
    ],
  },
  {
    version: "1.0.1",
    date: "2025-12-04",
    type: "patch",
    highlights: "Performance optimizations and Data Browser enhancements",
    changes: [
      {
        category: "feature",
        description:
          "Server-side pagination for Data Browser with customizable page sizes",
      },
      {
        category: "feature",
        description:
          "Redis Visualizer with real-time metrics: memory usage, ops/sec, hit rate, keyspace stats",
      },
      {
        category: "feature",
        description:
          "Redis Lua scripting support with EVAL/EVALSHA syntax highlighting",
      },
      {
        category: "improvement",
        description:
          "Redis Key Browser performance: 100x faster loading via command pipelining",
      },
      {
        category: "improvement",
        description:
          "Page size preference now persists across tables and sessions",
      },
      {
        category: "improvement",
        description:
          "Filter panel with scrollable list and reset button for large filter sets",
      },
      {
        category: "improvement",
        description:
          "Custom Monaco language for Redis with embedded Lua highlighting",
      },
      {
        category: "fix",
        description:
          "Fixed tooltip errors when mouse leaves application window",
      },
      {
        category: "fix",
        description: "Fixed MongoDB data not displaying in Data Browser",
      },
    ],
  },
  {
    version: "1.0.0",
    date: "2025-12-02",
    type: "major",
    highlights: "Initial public release of Nexatice",
    changes: [
      {
        category: "feature",
        description:
          "Multi-database support: MySQL, PostgreSQL, MongoDB, SQLite, Redis",
      },
      {
        category: "feature",
        description:
          "Monaco-powered query editor with syntax highlighting & auto-completion",
      },
      {
        category: "feature",
        description: "Data Browser with inline editing and filtering",
      },
      {
        category: "feature",
        description: "Change Review system for safe database modifications",
      },
      {
        category: "feature",
        description: "Visual schema explorer with ER diagram visualizer",
      },
      {
        category: "feature",
        description: "Redis Key Browser with tree view and value editing",
      },
      { category: "feature", description: "Query history and saved snippets" },
      {
        category: "feature",
        description: "Run Selected / Run From Cursor query execution modes",
      },
      {
        category: "feature",
        description: "Multi-window support with tab detaching",
      },
      {
        category: "feature",
        description: "SSH tunnel support for secure connections",
      },
      {
        category: "feature",
        description: "SSL/TLS encryption for database connections",
      },
      {
        category: "feature",
        description: "Auto-update system for seamless upgrades",
      },
      {
        category: "feature",
        description: "Customizable themes and editor settings",
      },
      {
        category: "security",
        description: "Encrypted local storage for credentials",
      },
      { category: "security", description: "Machine-bound license activation" },
    ],
  },
];

const CATEGORY_ICONS = {
  feature: <Sparkles className="w-4 h-4 text-emerald-400" />,
  fix: <Bug className="w-4 h-4 text-rose-400" />,
  improvement: <Zap className="w-4 h-4 text-amber-400" />,
  security: <Shield className="w-4 h-4 text-blue-400" />,
};

const CATEGORY_LABELS = {
  feature: "New Feature",
  fix: "Bug Fix",
  improvement: "Improvement",
  security: "Security",
};

export function Changelog() {
  return (
    <div className="relative min-h-screen">
      <div className="noise-overlay" />
      <MeshGradient />
      <Navbar currentPage="legal" />

      <main className="relative z-10 pt-32 pb-20 px-6">
        <div className="max-w-3xl mx-auto">
          <a
            href="/"
            className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </a>

          <div className="mb-12">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Changelog
            </h1>
            <p className="text-zinc-400">
              Track all the updates and improvements to Nexatice
            </p>
          </div>

          <div className="changelog-list">
            {CHANGELOG.map((entry, index) => (
              <div key={entry.version} className="changelog-entry">
                <div className="changelog-header">
                  <div className="changelog-version">
                    <Tag className="w-5 h-5" />
                    <span>v{entry.version}</span>
                    <span className={`version-badge ${entry.type}`}>
                      {entry.type}
                    </span>
                  </div>
                  <div className="changelog-date">
                    <Calendar className="w-4 h-4" />
                    {new Date(entry.date).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </div>
                </div>

                {entry.highlights && (
                  <p className="changelog-highlights">{entry.highlights}</p>
                )}

                <ul className="changelog-changes">
                  {entry.changes.map((change, i) => (
                    <li key={i} className="changelog-change">
                      <span className="change-icon">
                        {CATEGORY_ICONS[change.category]}
                      </span>
                      <span className="change-category">
                        {CATEGORY_LABELS[change.category]}
                      </span>
                      <span className="change-description">
                        {change.description}
                      </span>
                    </li>
                  ))}
                </ul>

                {index < CHANGELOG.length - 1 && (
                  <div className="changelog-divider" />
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
